import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import {
  AlertCircle,
  BadgeCheck,
  Building2,
  Camera,
  CheckCircle2,
  Copy,
  Fingerprint,
  Loader2,
  Mail,
  RefreshCw,
  ShieldCheck,
  UploadCloud,
  User,
  X,
} from 'lucide-react';
import { useAuthStore } from '../stores/auth-store';
import { useUserStore } from '../stores/user-store';
import { cn } from '../utils/cn';
import { deriveExternalUserFlag } from '../utils/user-scope';

interface FaqProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ROLE_LABELS: Record<string, string> = {
  super_admin: '超级管理员',
  admin: '管理员',
  advanced: '高级用户',
  intermediate: '中级用户',
  uploader: '上传者',
  basic: '基础用户',
};

const TENANT_LABELS: Record<string, string> = {
  cn: '中国区',
  intl: '国际区',
};

export default function FaqProfileModal({ isOpen, onClose }: FaqProfileModalProps) {
  const { user } = useAuthStore();
  const { profile, isLoading, fetchProfile, updateProfile, updateAvatar } = useUserStore();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [language, setLanguage] = useState('zh-CN');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    if (!profile && user) {
      fetchProfile();
    }
  }, [fetchProfile, isOpen, profile, user]);

  useEffect(() => {
    if (!isOpen) return;
    setName(profile?.name || '');
    setPhoneNumber(profile?.phone_number || '');
    setLanguage(profile?.language_preference || 'zh-CN');
    setMessage(null);
  }, [isOpen, profile]);

  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const email = profile?.email || user?.email || '';
  const emailPrefix = email.split('@')[0] || 'user';
  const displayName = profile?.name || user?.user_metadata?.name || emailPrefix;
  const avatarUrl = profile?.avatar_url || user?.user_metadata?.avatar_url || '';
  const initial = (displayName[0] || 'U').toUpperCase();
  const wecomUserid = profile?.wecom_userid || user?.user_metadata?.wecom_userid || '';
  const roleLabel = profile?.role ? ROLE_LABELS[profile.role] || profile.role : '-';
  const tenantLabel = profile?.tenant ? TENANT_LABELS[profile.tenant] || profile.tenant : '-';
  const isExternalUser = deriveExternalUserFlag(email, profile?.is_external);
  const userTypeLabel = isExternalUser ? '外部用户' : '内部用户';

  async function handleSave() {
    setSaving(true);
    setMessage(null);
    try {
      await updateProfile({
        name: name.trim() || null,
        phone_number: phoneNumber.trim() || null,
        language_preference: language,
      });
      setMessage({ type: 'success', text: '个人资料已保存' });
    } catch (error) {
      setMessage({ type: 'error', text: (error as Error).message || '保存失败' });
    } finally {
      setSaving(false);
    }
  }

  async function handleRefresh() {
    setRefreshing(true);
    setMessage(null);
    try {
      await fetchProfile();
      setMessage({ type: 'success', text: '资料已同步' });
    } catch (error) {
      setMessage({ type: 'error', text: (error as Error).message || '同步失败' });
    } finally {
      setRefreshing(false);
    }
  }

  async function handleAvatarChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: '请选择图片文件' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: '头像不能超过 5MB' });
      return;
    }

    setUploading(true);
    setMessage(null);
    try {
      await updateAvatar(file);
      setMessage({ type: 'success', text: '头像已更新' });
    } catch (error) {
      setMessage({ type: 'error', text: (error as Error).message || '上传失败' });
    } finally {
      setUploading(false);
    }
  }

  async function copyValue(label: string, value: string) {
    if (!value) return;
    await navigator.clipboard.writeText(value);
    setCopied(label);
    window.setTimeout(() => setCopied(null), 1200);
  }

  const identityRows = [
    { label: '绑定邮箱', value: email, icon: Mail, copyKey: 'email' },
    { label: '企业微信 UserID', value: wecomUserid || '未绑定', icon: Fingerprint, copyKey: 'wecom' },
    { label: '系统 UID', value: profile?.id || user?.id || '-', icon: ShieldCheck, copyKey: 'uid' },
  ];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-6">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/35"
        aria-label="关闭个人中心"
        onClick={onClose}
      />
      <section className="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <header className="flex shrink-0 items-center justify-between border-b border-slate-100 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
              <User size={20} />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-800">个人中心</h2>
              <p className="text-xs text-slate-400">FAQ 企业账号资料</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
            aria-label="关闭"
          >
            <X size={18} />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="grid gap-0 md:grid-cols-[260px_1fr]">
            <aside className="border-b border-slate-100 bg-slate-50/70 px-5 py-6 md:border-b-0 md:border-r">
              <div className="flex flex-col items-center text-center">
                <div className="relative">
                  <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-sky-400 to-indigo-500 text-3xl font-bold text-white shadow-sm">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt={displayName} className="h-full w-full object-cover" />
                    ) : (
                      initial
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="absolute -bottom-2 -right-2 flex h-9 w-9 items-center justify-center rounded-full border-2 border-white bg-sky-500 text-white shadow-sm transition hover:bg-sky-600 disabled:cursor-not-allowed disabled:opacity-60"
                    aria-label="上传头像"
                    title="上传头像"
                  >
                    {uploading ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarChange}
                  />
                </div>
                <div className="mt-5 min-w-0">
                  <p className="truncate text-lg font-bold text-slate-800">{displayName}</p>
                  <p className="mt-1 truncate text-xs text-slate-500">{email}</p>
                </div>
                <div className="mt-4 flex flex-wrap justify-center gap-2">
                  <span className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-600">
                    <BadgeCheck size={13} />
                    {roleLabel}
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-500">
                    <Building2 size={13} />
                    {tenantLabel}
                  </span>
                  <span className={cn(
                    'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold',
                    isExternalUser
                      ? 'border-amber-200 bg-amber-50 text-amber-600'
                      : 'border-sky-200 bg-sky-50 text-sky-600',
                  )}>
                    <Fingerprint size={13} />
                    {userTypeLabel}
                  </span>
                </div>
              </div>

              <div className="mt-6 space-y-2 border-t border-slate-200 pt-4">
                <InfoLine label="用户类型" value={userTypeLabel} />
                <InfoLine label="FAQ 管理员" value={profile?.is_faq_admin ? '是' : '否'} />
                <InfoLine label="全部租户" value={profile?.can_access_all_tenants ? '可访问' : '不可访问'} />
                <InfoLine label="双重验证" value={profile?.two_factor_enabled ? '已开启' : '未开启'} />
              </div>
            </aside>

            <div className="space-y-6 px-5 py-6">
              {message && (
                <div
                  className={cn(
                    'flex items-start gap-2 rounded-xl border px-3 py-2 text-sm',
                    message.type === 'success'
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                      : 'border-red-200 bg-red-50 text-red-600',
                  )}
                >
                  {message.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                  <span>{message.text}</span>
                </div>
              )}

              <section>
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-sm font-bold text-slate-700">基础资料</h3>
                  <button
                    type="button"
                    onClick={handleRefresh}
                    disabled={refreshing || isLoading}
                    className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-semibold text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-60"
                  >
                    <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
                    同步
                  </button>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="space-y-1.5">
                    <span className="text-xs font-semibold text-slate-500">用户名</span>
                    <input
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                      placeholder="请输入用户名"
                    />
                  </label>
                  <label className="space-y-1.5">
                    <span className="text-xs font-semibold text-slate-500">手机号</span>
                    <input
                      value={phoneNumber}
                      onChange={(event) => setPhoneNumber(event.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                      placeholder="未填写"
                    />
                  </label>
                  <label className="space-y-1.5 sm:col-span-2">
                    <span className="text-xs font-semibold text-slate-500">语言偏好</span>
                    <select
                      value={language}
                      onChange={(event) => setLanguage(event.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                    >
                      <option value="zh-CN">简体中文</option>
                      <option value="en-US">English</option>
                    </select>
                  </label>
                </div>
              </section>

              <section>
                <h3 className="mb-3 text-sm font-bold text-slate-700">企业身份</h3>
                <div className="divide-y divide-slate-100 rounded-xl border border-slate-200">
                  {identityRows.map((row) => (
                    <div key={row.label} className="flex min-h-[54px] items-center gap-3 px-3 py-2.5">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-slate-400">
                        <row.icon size={16} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-semibold text-slate-400">{row.label}</p>
                        <p className="truncate text-sm font-semibold text-slate-700">{row.value}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => copyValue(row.copyKey, row.value)}
                        disabled={!row.value || row.value === '未绑定' || row.value === '-'}
                        className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-40"
                        aria-label={`复制${row.label}`}
                        title={copied === row.copyKey ? '已复制' : '复制'}
                      >
                        {copied === row.copyKey ? <CheckCircle2 size={15} /> : <Copy size={15} />}
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </div>

        <footer className="flex shrink-0 items-center justify-between border-t border-slate-100 bg-white px-5 py-4">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-60"
          >
            <UploadCloud size={16} />
            上传头像
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-500 transition hover:bg-slate-50"
            >
              关闭
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || isLoading}
              className="inline-flex items-center gap-2 rounded-xl bg-sky-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-sky-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
              保存资料
            </button>
          </div>
        </footer>
      </section>
    </div>
  );
}

function InfoLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 text-xs">
      <span className="text-slate-400">{label}</span>
      <span className="font-semibold text-slate-600">{value}</span>
    </div>
  );
}
